import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/*
 * MPH-1321: the room photos that failed to load, kept once for the rooms section, so every room card and
 * room details gallery agrees on them: a photo that failed in one is neither requested again nor counted by
 * the others. A photo is told apart by the room's own image url, whatever crop of it failed.
 *
 * A failure may only be a short connection loss, so the url that failed is tried again in the background, a
 * few times with a growing delay and once more whenever the browser comes back online. A photo that loads
 * then is given back to every card and gallery; one that keeps failing stays out.
 */

/** The delays before each background retry of a photo that failed to load. */
export const FAILED_PHOTO_RETRY_DELAYS_MS = [2000, 4000, 8000];

type FailedRoomPhotos = {
  /** The room image urls that failed to load, in the order they failed. */
  failedImages: ReadonlySet<string>;
  /** Called by an image of the room photo `image` whose url `src` failed to load. */
  onImageFallback: (image: string, src: string) => void;
};

type Retry = { src: string; attempts: number; pending: boolean; failed: boolean };

const FailedRoomPhotosContext = createContext<FailedRoomPhotos | null>(null);

function useFailedRoomPhotosStore(enabled: boolean): FailedRoomPhotos {
  const [failedImages, setFailedImages] = useState<ReadonlySet<string>>(() => new Set());
  // Every photo that ever failed, with the retries it had: kept once it loads again, so a photo that keeps
  // coming and going is not retried for ever.
  const retries = useRef(new Map<string, Retry>());
  // The retries waiting for their delay, by photo.
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // No retry is started once the store is gone, nor by the stand-in store of a component that uses the
  // shared one.
  const active = useRef(false);

  const scheduleRetry = useCallback(function schedule(image: string, delay: number) {
    const retry = retries.current.get(image);
    if (!retry || retry.pending || !active.current) return;
    retry.pending = true;
    const timer = setTimeout(() => {
      timers.current.delete(image);
      retry.attempts += 1;
      const probe = new Image();
      probe.onload = () => {
        retry.pending = false;
        retry.failed = false;
        setFailedImages((failed) => {
          if (!failed.has(image)) return failed;
          const next = new Set(failed);
          next.delete(image);
          return next;
        });
      };
      probe.onerror = () => {
        retry.pending = false;
        if (retry.attempts < FAILED_PHOTO_RETRY_DELAYS_MS.length) {
          schedule(image, FAILED_PHOTO_RETRY_DELAYS_MS[retry.attempts]);
        }
      };
      probe.src = retry.src;
    }, delay);
    timers.current.set(image, timer);
  }, []);

  const onImageFallback = useCallback(
    (image: string, src: string) => {
      setFailedImages((failed) => (failed.has(image) ? failed : new Set(failed).add(image)));
      const retry = retries.current.get(image) ?? { src, attempts: 0, pending: false, failed: true };
      retries.current.set(image, retry);
      // Another image of the same photo, which had already started loading, failed after it.
      if (retry.pending) return;
      retry.src = src;
      retry.failed = true;
      if (retry.attempts < FAILED_PHOTO_RETRY_DELAYS_MS.length) {
        scheduleRetry(image, FAILED_PHOTO_RETRY_DELAYS_MS[retry.attempts]);
      }
    },
    [scheduleRetry]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    active.current = true;
    // Back online, the photos that failed get their retries again, starting now.
    const onOnline = () => {
      Array.from(retries.current.keys()).forEach((image) => {
        const retry = retries.current.get(image);
        if (!retry?.failed || retry.pending) return;
        retry.attempts = 0;
        scheduleRetry(image, 0);
      });
    };
    window.addEventListener('online', onOnline);
    const pendingTimers = timers.current;
    const retriesByImage = retries.current;
    return () => {
      active.current = false;
      window.removeEventListener('online', onOnline);
      pendingTimers.forEach((timer, image) => {
        clearTimeout(timer);
        const retry = retriesByImage.get(image);
        if (retry) retry.pending = false;
      });
      pendingTimers.clear();
    };
  }, [enabled, scheduleRetry]);

  return useMemo(() => ({ failedImages, onImageFallback }), [failedImages, onImageFallback]);
}

/** Shares one record of the room photos that failed to load among the room cards and galleries inside it. */
export function FailedRoomPhotosProvider({ children }: { children: React.ReactNode }) {
  const failedRoomPhotos = useFailedRoomPhotosStore(true);
  return <FailedRoomPhotosContext.Provider value={failedRoomPhotos}>{children}</FailedRoomPhotosContext.Provider>;
}

/**
 * MPH-1321: the images a room card or gallery should still show, which are the ones not known to fail to
 * load, plus the handler its images call when one fails. Counters then count only these. Outside a
 * FailedRoomPhotosProvider (a story, a unit test) the component keeps its own record.
 */
export function useLoadableImages(images: readonly string[]) {
  const shared = useContext(FailedRoomPhotosContext);
  const own = useFailedRoomPhotosStore(!shared);
  const { failedImages, onImageFallback } = shared ?? own;
  const loadableImages = useMemo(() => images.filter((image) => !failedImages.has(image)), [images, failedImages]);
  return { loadableImages, failedImages, onImageFallback };
}
